import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import { Loader2, ArrowLeft } from 'lucide-react';

export default function Login() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authAPI.login(phone, password);
      const authData = response.data.data;
      login(authData);
      toast.success('Login successful!');
      if (authData.user.role === 'ROLE_ADMIN') navigate('/admin/dashboard');
      else if (authData.user.status === 'PARTIAL_REGISTERED') navigate('/complete-registration');
      else navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">

      {/* ── LEFT: Branding ─────────────────────────────────────── */}
      <div className="hidden lg:flex w-[45%] flex-col justify-between px-16 py-12
        bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 20% 60%, rgba(20,184,166,0.18) 0%, transparent 65%),
                         radial-gradient(ellipse at 80% 20%, rgba(14,165,233,0.12) 0%, transparent 60%)`
          }} />

        {/* Logo */}
        <div className="relative">
          <img src="/logo-white.png" alt="ShramSena" className="h-11 w-auto" />
        </div>

        {/* Centre copy */}
        <div className="relative space-y-6">
          <div>
            <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Healthcare Staff Portal
            </p>
            <h2 className="text-4xl font-bold text-white leading-snug">
              Your next opportunity<br />
              <span className="text-teal-400">starts here.</span>
            </h2>
          </div>
          <p className="text-slate-400 text-base leading-relaxed max-w-sm">
            Join India's growing network of verified healthcare professionals — nurses,
            caregivers, physiotherapists and more.
          </p>
          <div className="space-y-3 pt-2">
            {[
              'Free to register',
              'Admin-verified profile',
              'Direct employer connections',
              'Opportunities across 20+ cities',
            ].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-teal-500/20 border border-teal-500/40
                  flex items-center justify-center shrink-0">
                  <div className="w-2 h-2 rounded-full bg-teal-400" />
                </div>
                <span className="text-slate-300 text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-slate-600 text-xs">
          © {new Date().getFullYear()} ShramSena™ — Your Trusted Healthcare Staffing Partner
        </p>
      </div>

      {/* ── VERTICAL DIVIDER ───────────────────────────────────── */}
      <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />

      {/* ── RIGHT: Form ────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-12">
        <div className="w-full max-w-sm mx-auto">

          {/* Mobile logo */}
          <div className="lg:hidden mb-8">
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-9 w-auto" />
          </div>

          <button onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600
              text-sm mb-10 transition group">
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
            Back to Home
          </button>

          <div className="mb-8">
            <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.15em] mb-2">
              Staff &amp; Admin
            </p>
            <h1 className="text-3xl font-bold text-slate-900 mb-1">Welcome back</h1>
            <p className="text-gray-400 text-sm">Sign in to your ShramSena account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Phone Number
              </label>
              <input
                type="tel" value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="9876543210" required maxLength={10}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50
                  focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                  placeholder-gray-300 transition"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Password
              </label>
              <input
                type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password" required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50
                  focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                  placeholder-gray-300 transition"
              />
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3.5 rounded-xl font-bold text-sm text-white
                bg-gradient-to-r from-teal-600 to-cyan-600
                hover:from-teal-500 hover:to-cyan-500
                shadow-lg shadow-teal-500/25
                flex items-center justify-center gap-2
                disabled:opacity-60 transition mt-2">
              {loading
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in...</>
                : 'Sign In'}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 space-y-3 text-sm text-gray-400">
            <p>
              Forgot your password?{' '}
              <Link to="/forgot-password" className="text-teal-600 font-semibold hover:underline">
                Reset via OTP
              </Link>
            </p>
            <p>
              New to ShramSena?{' '}
              <Link to="/register" className="text-teal-600 font-semibold hover:underline">
                Register as Staff
              </Link>
            </p>
            <p>
              Organization login?{' '}
              <Link to="/company/login" className="text-teal-600 font-semibold hover:underline">
                Organization portal →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
